import { ClientFeature } from "../../parsers/login";
import { IAlias } from "../../api/ResolveApi/resolve-api.types";
import { ICourseLocalizedText, ICourseSet, IPublicationInfo } from "../../components/course-author/types";
import { PreferredLocale } from "../../utils/locales";
import { AliasTag, EmailAddressTag } from "../AccountManagement/types";

export interface IFontFamily {
  ffmId: string;
  displayName: string;
}

export interface ICourseInstance {
  courseInstanceId: string;
  courseId: string;
}

export interface ICourse {
  courseId: string;
  createdAt: string;
  displayName: string;
  locked: boolean;
  masterLocale: PreferredLocale;
  supportedLocales: PreferredLocale[];
  publicationInfo: IPublicationInfo | null;
  lastEditorName: string;
  latestRevId: number;
}

export interface IAccount {
  canonicalAlias: IAlias | null;
  deleted: boolean;
  displayName: string;
  id: string;
  locked: boolean;
  owner: string;
}

export interface IClass {
  admins: IAccount[] | string[];
  classId: string;
  displayName: string;
  courseIds: string[];
  goals: {
    elems: IClassGoalResponse[];
    nextId: number;
  };
  members: IAccount[] | string[];
  versionId: number;
}

export interface IClassGoalResponse {
  id: number;
  course: string;
  displayName: string;
  courseInstEnv: {
    allowCourseReview: boolean;
    assumePriorKnowledge: boolean;
    includeBranding: boolean;
    useStrictMode: boolean;
  };
  deleted: boolean;
  description: string;
}

export enum RequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export interface IOrganizationAliasesState {
  alias: { kind: string; name: string };
  namespace: string;
  tag: { tag: string; content: string | null };
}

export interface IOrganizationAliasesElems {
  id: number;
  state: IOrganizationAliasesState;
}

export interface IOrganizationAliases {
  elems: IOrganizationAliasesElems[];
  nextId: number;
}

export interface ICompleteOrganization {
  admins: string[];
  aliases: IOrganizationAliases;
  displayName: string | null;
  defaultLocale: PreferredLocale;
  deleted: boolean;
  organizationId: string;
  owner: string;
  releaseLine: string;
  scormSettings: IScormSettings;
  storeLearnerLog: boolean;
  style: string | null;
  versionId: number;
}

export interface IScormSettings {
  hideNavBar: boolean;
  lms: string;
  primaryKey: string;
  scormVersion: string;
}

export type ScormPackageState = "open" | "closed";

export interface IScormPackage {
  scormPackageId: string;
  courseId: string;
  completedExitText: ICourseSet<ICourseLocalizedText>;
  expectedNumUsers: number | null;
  hideLmsNavbar: boolean;
  inProgressExitText: ICourseSet<ICourseLocalizedText>;
  name: string;
  lms: string;
  scormVersion: string;
  state: ScormPackageState;
  versionId: number;
  useCustomCompletedExitText: boolean;
  useCustomInProgressExitText: boolean;
  includeBranding: boolean;
  allowCourseReview: boolean;
  useStrictMode: boolean;
  locked: boolean;
}

export interface IChangeOrganizationName {
  newOrganizationName: string;
  oldOrganizationName: string;
  organizationId: string;
}

interface IEmailAddressState {
  id: number;
  state: {
    tag: EmailAddressTag;
    address: string;
  };
  deleted?: boolean;
}

export interface IAliasState {
  tag: {
    tag: AliasTag;
    content: { kind: string; name: string } | null;
  };
  namespace: string;
  alias: { kind: string; name: string };
}

export interface IAliasElem {
  id: number;
  deleted?: boolean;
  state: IAliasState;
}

export interface IUserAccount {
  aliases: {
    elems: IAliasElem[];
    nextId: number;
  };
  deleted: boolean;
  displayName: string;
  emailAddresses: {
    elems: IEmailAddressState[];
    nextId: number;
  };
  kind: {
    tag: string;
  };
  oldClientFeatures: ClientFeature[];
  owner: string;
  preferredLocale: PreferredLocale | null;
  primaryEmailAddress: string | null;
  usedScormPackages: string[];
}

export interface IFullUserAccount extends IUserAccount {
  accountId: string;
}

export interface IUserAccountInfo {
  displayName: string;
  createdAt: string;
  lastEdited: string;
  lastEditorName: string;
}

export interface IUserInfo extends IUserAccountInfo {
  accountId: string;
}
